import { useEffect, useMemo, useRef, useState } from 'react';
import { useSetDocumentTitle } from "../../../hooks/useSetDocumentTitle";
import { first, difference } from 'lodash-es';
import { Drawer, Button, Callout, Spinner, Typography, useDidUpdate, useStateFromUrl, ConfirmationModal, Row } from 'seagull';
import EmptyState from "../../../components/EmptyState/EmptyState";
import GenericError from "../../../components/GenericError/GenericError";
import RestfulWrapper from "../../../components/RestfulWrapper/RestfulWrapper";
import BillingControl from "./BillingControl/BillingControl";
import InvoiceNavigator from "./InvoiceNavigator/InvoiceNavigator";
import ScheduledInvoice from "./ScheduledInvoice/ScheduledInvoice";
import MinisiteTabHeader from "../MinisiteTabHeader/MinisiteTabHeader";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { formatNumericUSDate } from "../../../utils";
import BillingControlFooter from "./BillingControl/BillingControlFooter";
import DisabledBillingTooltip from "../../../components/DisabledBillingTooltip";
import { useSubscribeToBillingObservable } from "../../../providers/BillingObservableProvider";
import { ErrorResponse, ServerError } from "../../../infra/ErrorResponse";
import { timeModel, BillingControlDataModel } from "../../../models";
import { RestfulStrategy, useRestfulWrapper } from "../../../hooks/useRestfulWrapper";
import { useOverlayContext, useIoContext, useStoreActions, useStore } from "../../../contexts";
import { BillingHubUrlParams, getEmptyState, getInvoiceDateInfo, getLineItemsProps, getNoInvoicesText } from "./BillingHub.utils";
import { selectors } from "../../../store/state-selectors";
import AttachFiles from "../../../components/AttachFiles/AttachFiles";
import { Accounting } from "../../../types/api.model";
import styles from "./BillingHub.module.scss";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
function BillingHubWithData(props) {
  const {
    agreement,
    billingData,
    isVendor,
    onRefresh: refreshBillingHubData
  } = props;
  const {
    invoiceDraftDates,
    billingControlData
  } = billingData;
  const {
    api,
    logBI
  } = useStoreActions();
  const {
    getNow
  } = useIoContext();
  const {
    DISABLE_BILLING_SCAN,
    ATTACH_FILES
  } = useFeatureFlags();
  const overlayContext = useOverlayContext();
  const lineItemIds = useRef();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const {
    urlState: {
      [BillingHubUrlParams.SelectedDate]: urlSelectedDate
    },
    changeUrlState
  } = useStateFromUrl({
    [BillingHubUrlParams.SelectedDate]: {
      defaultValue: invoiceDraftDates.dates()[0] ? formatNumericUSDate(invoiceDraftDates.dates()[0]) : '',
      applyDefaultOnlyOnMount: true
    }
  });
  useEffect(() => {
    const date = new Date(urlSelectedDate);
    setSelectedDate(isNaN(date.getTime()) ? null : date);
  }, [urlSelectedDate]);
  const {
    loading: loadingInvoiceDraft,
    refresh: refreshInvoiceDraft,
    renderRestfulState: renderScheduledInvoice
  } = useRestfulWrapper({
    fetch: fetchInvoiceDraft
  });
  const invoiceDateInfo = getInvoiceDateInfo(selectedDate, billingData.invoiceDraftDates);
  const isActiveAgreement = useMemo(() => !agreement.effectiveDate || timeModel.fromDateStampObject(agreement.effectiveDate).getTime() <= getNow().getTime(), [agreement.effectiveDate]);
  useSubscribeToBillingObservable(() => refreshAllData({
    silent: true
  }));
  useEffect(() => {
    const selectedDate = !urlSelectedDate && invoiceDraftDates !== null && invoiceDraftDates !== void 0 && invoiceDraftDates.dates().length ? formatNumericUSDate(invoiceDraftDates.dates()[0]) : urlSelectedDate && !(invoiceDraftDates !== null && invoiceDraftDates !== void 0 && invoiceDraftDates.dates().length) ? '' : urlSelectedDate;
    changeUrlState({
      selectedDate
    });
  }, [invoiceDraftDates]);
  useDidUpdate(() => {
    refreshInvoiceDraft();
  }, [selectedDate]);
  async function fetchInvoiceDraft() {
    const getAddedLineItemId = newInvoiceDraft => {
      // used for blink animation
      const newLineItemIds = newInvoiceDraft.lineItems.map(_ref => {
        let {
          id
        } = _ref;
        return id;
      });
      const newAddedLineItemId = lineItemIds.current ? first(difference(newLineItemIds, lineItemIds.current)) : undefined;
      lineItemIds.current = newLineItemIds;
      return newAddedLineItemId;
    };
    if (selectedDate) {
      const newInvoiceDraft = await api.invoiceDraft.getByDate(agreement.id, selectedDate);
      return {
        invoiceDraft: newInvoiceDraft,
        addedLineItemId: newInvoiceDraft ? getAddedLineItemId(newInvoiceDraft) : undefined
      };
    } else {
      return {
        invoiceDraft: null
      };
    }
  }
  async function onApprove(chargeId) {
    logBI(10569);
    const loaderName = 'approveCharge';
    overlayContext.startLoader(loaderName);
    try {
      await api.charge.approve(agreement.id, chargeId);
      await refreshAllData();
      overlayContext.showSuccessToast('Charge approved', 'approve-success-toast');
    } catch (err) {
      overlayContext.showErrorToast('Failed to approve charge', 'approve-failure-toast');
    } finally {
      overlayContext.stopLoader(loaderName);
    }
  }
  async function onRemove(chargeId, options) {
    const {
      billLaterCommandId,
      serviceId
    } = options;
    const confirmed = await overlayContext.showModal({
      closeOnClickOutside: true,
      shouldBlurBackground: true,
      closeOnEscapeKey: true,
      content: _ref2 => {
        let {
          dismissModal
        } = _ref2;
        return /*#__PURE__*/_jsx(ConfirmationModal, {
          skin: "danger",
          title: "Remove line item",
          content: "Are you sure you want to remove this line item?",
          primaryButtonLabel: "Remove",
          secondaryButtonLabel: "Cancel",
          onPrimaryButtonClick: () => dismissModal(true),
          onSecondaryButtonClick: () => dismissModal(false)
        });
      }
    });
    if (!confirmed) return;
    const loaderName = 'removeCharge';
    overlayContext.startLoader(loaderName);
    const isService = !!serviceId;
    try {
      if (isService && billLaterCommandId) {
        logBI(10570);
        await api.billCommand.remove(agreement.id, serviceId, billLaterCommandId);
      } else {
        logBI(10567);
        await api.charge.delete(agreement.id, chargeId);
      }
      await refreshAllData();
      overlayContext.showSuccessToast(isService ? 'Service was removed from upcoming bill' : 'Charge was removed', 'remove-success-toast');
    } catch (err) {
      const e = new ErrorResponse(err);
      const errorText = e.hasServerError(ServerError.AdHocChargeNotFound) ? 'Failed to remove charge. It was already removed' : isService ? 'Failed to remove service from upcoming bill' : 'Failed to remove charge';
      overlayContext.showErrorToast(errorText, 'remove-failure-toast');
    } finally {
      overlayContext.stopLoader(loaderName);
    }
  }
  async function refreshAllData(options) {
    await Promise.all([refreshInvoiceDraft(options), refreshBillingHubData(options)]);
  }
  return /*#__PURE__*/_jsxs("div", {
    "data-testid": "billing-hub",
    children: [/*#__PURE__*/_jsx(MinisiteTabHeader, {
      appcuesId: "billing-hub-header",
      text: invoiceDraftDates.dates().length > 0 && isVendor && /*#__PURE__*/_jsx(InvoiceNavigator, {
        value: selectedDate,
        options: invoiceDraftDates.dates(),
        onChange: date => {
          lineItemIds.current = undefined;
          changeUrlState({
            selectedDate: date ? formatNumericUSDate(date) : ''
          });
        }
      }),
      actions: isVendor && (!ATTACH_FILES ? /*#__PURE__*/_jsx("div", {
        "data-appcuesid": "billing-hub-actions",
        children: /*#__PURE__*/_jsx(Button, {
          skin: "primaryOutline",
          disabled: DISABLE_BILLING_SCAN || loadingInvoiceDraft,
          onClick: () => setIsDrawerOpen(true),
          "data-testid": "manage-billing-button",
          tooltip: DISABLE_BILLING_SCAN ? {
            title: /*#__PURE__*/_jsx(DisabledBillingTooltip, {}),
            placement: 'left'
          } : undefined,
          children: "Billing Control"
        })
      }) : /*#__PURE__*/_jsx(_Fragment, {
        children: /*#__PURE__*/_jsx("div", {
          "data-testid": "billing-hub-actions",
          "data-appcuesid": "billing-hub-actions",
          style: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          },
          children: /*#__PURE__*/_jsxs(Row, {
            gap: 16,
            children: [/*#__PURE__*/_jsx(AttachFiles, {
              onSubmit: () => {},
              testId: "attach-files-button"
            }), /*#__PURE__*/_jsx(Button, {
              skin: "primaryOutline",
              disabled: DISABLE_BILLING_SCAN || loadingInvoiceDraft,
              onClick: () => setIsDrawerOpen(true),
              "data-testid": "manage-billing-button",
              tooltip: DISABLE_BILLING_SCAN ? {
                title: /*#__PURE__*/_jsx(DisabledBillingTooltip, {}),
                placement: 'left'
              } : undefined,
              children: "Billing Control"
            })]
          })
        })
      }))
    }), isVendor && !isActiveAgreement && /*#__PURE__*/_jsxs(Callout, {
      className: styles.callout,
      "data-testid": "bh-future-agreement-warning",
      children: ["The agreement takes effect on ", timeModel.formatDate(agreement.effectiveDate), ". Until then, you can add or remove services from future invoices using the Billing Control."]
    }), /*#__PURE__*/_jsx(Drawer, {
      open: isDrawerOpen,
      header: /*#__PURE__*/_jsx(Typography, {
        variant: "h6",
        weight: "normal",
        children: "Billing Control"
      }),
      footer: /*#__PURE__*/_jsx(BillingControlFooter, {
        agreement: agreement,
        invoiceDateInfo: invoiceDateInfo,
        earliestBillableDate: invoiceDraftDates.earliestBillableDate(),
        controlData: billingControlData
      }),
      onClose: () => setIsDrawerOpen(false),
      onCloseButtonClick: () => setIsDrawerOpen(false),
      BackdropProps: {
        invisible: true
      },
      children: /*#__PURE__*/_jsx(BillingControl, {
        agreement: agreement,
        billingControlData: billingControlData,
        invoiceDateInfo: invoiceDateInfo,
        onRefresh: refreshAllData,
        earliestBillableDate: invoiceDraftDates.earliestBillableDate()
      })
    }), renderScheduledInvoice({
      strategy: RestfulStrategy.PreferLoading,
      renderChild: _ref3 => {
        let {
          data
        } = _ref3;
        return data.invoiceDraft ? /*#__PURE__*/_jsx(ScheduledInvoice, {
          agreementId: agreement.id,
          isVendor: isVendor,
          issueDate: data.invoiceDraft.issueDate,
          paymentDate: data.invoiceDraft.paymentDate,
          rowsProps: getLineItemsProps(data.invoiceDraft, onApprove, onRemove, isVendor),
          addedLineItemId: data.addedLineItemId,
          totalAmount: data.invoiceDraft.totalAmount,
          onRefresh: () => Promise.resolve(undefined),
          attachments: ATTACH_FILES ? {
            files: [{
              name: 'receipt.pdf',
              url: 'https://example.com/receipt.pdf',
              size: 1024,
              id: '1',
              loading: false
            }],
            onRemove: () => {}
          } : undefined
        }) : /*#__PURE__*/_jsx(Typography, {
          className: styles.empty,
          "data-testid": "no-invoices",
          children: getNoInvoicesText(selectedDate, isVendor)
        });
      },
      renderError: () => /*#__PURE__*/_jsx(GenericError, {
        testid: "error-boundary-page"
      }),
      renderLoader: () => /*#__PURE__*/_jsx("div", {
        className: styles.loader,
        "data-testid": "invoice-draft-loading",
        children: /*#__PURE__*/_jsx(Spinner, {})
      })
    })]
  });
}
export default function BillingHub(props) {
  const {
    agreement,
    isVendor,
    refreshAgreement
  } = props;
  const {
    api
  } = useStoreActions();
  const emptyState = getEmptyState(agreement);
  const isXeroLinked = useStore(selectors.integration.showXeroIntegration);
  const isQboLinked = useStore(selectors.integration.showQboIntegration);
  const {
    fetchQboClasses,
    fetchAccountingItems,
    fetchQboTimeActivities
  } = useStoreActions();
  const {
    XERO_INTEGRATION
  } = useFeatureFlags();
  const {
    SERVICE_TEMPLATE_BUNDLE
  } = useFeatureFlags();
  useSetDocumentTitle('Billing Hub');
  async function fetchBillingHubData() {
    const [invoiceDraftDates, billingControlData] = await Promise.all([api.invoiceDraft.getDates(agreement.id), isVendor ? api.relationship.getBillingControlData(agreement.id) : Promise.resolve(BillingControlDataModel.make()), isQboLinked && fetchAccountingItems(Accounting.QBO, !!SERVICE_TEMPLATE_BUNDLE), XERO_INTEGRATION && isXeroLinked && fetchAccountingItems(Accounting.Xero, !!SERVICE_TEMPLATE_BUNDLE), isQboLinked && fetchQboClasses(), isQboLinked && fetchQboTimeActivities(agreement.id)]);
    return {
      invoiceDraftDates,
      billingControlData
    };
  }
  return emptyState ? /*#__PURE__*/_jsx(EmptyState, {
    testid: "billing-hub-empty-state",
    illustration: "UpcomingBill",
    title: emptyState.text,
    paragraphs: emptyState.paragraphs
  }) : /*#__PURE__*/_jsx(RestfulWrapper, {
    fetch: fetchBillingHubData,
    renderLoader: () => /*#__PURE__*/_jsx(Spinner, {
      centeredInBox: true,
      centerBoxHeight: 300
    }),
    renderChild: _ref4 => {
      let {
        data,
        loading,
        refresh
      } = _ref4;
      return /*#__PURE__*/_jsx(BillingHubWithData, {
        billingData: data,
        agreement: agreement,
        isVendor: isVendor,
        isRefreshing: loading,
        onRefresh: async options => {
          await Promise.all([refresh(options), refreshAgreement(options)]);
        }
      });
    }
  });
}